import React, { useContext, useEffect } from "react";
import { Modal } from "rsuite";
import EventDate from "../../atoms/MUI/Date";
import { useState } from "react";
import { AppContext } from "../../provider/AppContext";
import AAutoComplete from "../../atoms/MUI/AutoComplete";
import { Grid, InputAdornment, Popover, Box } from "@mui/material";
import LabelIcon from "@mui/icons-material/Label";
import { extractIds, generateToast } from "../../helper";
import { ERROR, isCollaboratorClientType } from "../../constant";
import { createContract, cloneContract } from "../../services/ContractServices";
import { createClientNickname, getClientNicknames } from "../../services/ClientService";
import PublicIcon from "@mui/icons-material/Public";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { useTheme } from "../../ThemeContext";
import { Loader } from "rsuite";
import { useDispatch, useSelector } from "react-redux";
import { toggleTrigger } from "../../redux/reducers/EventReducer";
import { executiveRoles } from "../NavBar/Navbar";
import TextField from "@mui/material/TextField";
import { handleParentFilter } from "../../redux/reducers/ContractsReducer";

export const initialState = {};
export const rentalInitial = [
  {
    expenseHead: "Rental",
  },
  {
    expenseHead: "Staff",
  },
  {
    expenseHead: "Cleaning",
  },
];

export const foodInitial = [
  {
    expenseHead: "Appetizers",
    shouldShowOnContract: true,
  },
  {
    expenseHead: "Dinner",
    shouldShowOnContract: true,
  },
];

export const bevInitial = [
  {
    expenseHead: "Bar Tab",
  },
  {
    expenseHead: "Client Drinks",
  },
];

const ContactForm = ({ setShowForm, showForm, handleClick, cloneData }) => {
  const handleClose = () => {
    setIsLoading(false);
    setShowForm(false);
  };
  const dispatch = useDispatch();
  const themeToggle = useTheme();
  const mode = themeToggle.themeState?.mode;
  const [isLoading, setIsLoading] = useState(false);
  const {
    getRegion,
    getClientType,
    user,
    salesEmpWithOptions,
    fetchParentOptions,
    getSchoolName,
    fetchDetails,
    getClientName,
    updateClientName,
    updateClientType,
    updateRegion,
    updateSchool,
  } = useContext(AppContext);

  const {
    Contracts: { parentFilter },
  } = useSelector((state) => state);

  const handleDispatch = () => {
    dispatch(handleParentFilter({ ...parentFilter }));
  };

  const isParentForm = showForm === 2;
  const [summary, setSummary] = useState({
    startDate: "",
    ...(cloneData ? {
      clientType: cloneData.clientType,
      region: cloneData.region,
      soldBy: cloneData.soldBy,
      eventClients: cloneData.eventClients?.map(client => ({
        ...client,
        client: client.client,
        name: client.client?.name
      })),
      startDate: cloneData.startDate,
    } : {})
  });

  const onChange = (e) => {
    const obj = {
      ...summary,
      ...e,
    };
    setSummary(obj);
  };

  const handelClient = (e) => {
    onChange({
      eventClients: [
        {
          ...(summary?.eventClients?.[0] || {}),
          ...e?.eventClients,
          client: {
            id: e?.eventClients.id,
            name: e?.eventClients.name,
            nicknames: e?.eventClients.nicknames || [],
          },
          nicknameId: null,
          nickname: null,
        },
      ],
    });
  };

  const handleNicknameSelect = async (selected) => {
    const clientId = summary?.eventClients?.[0]?.client?.id;
    if (!clientId) return;

    // If cleared or master name selected
    if (!selected?.nickname || selected.nickname.name === summary?.eventClients?.[0]?.client?.name) {
      onChange({
        eventClients: [
          {
            ...(summary?.eventClients?.[0] || {}),
            nicknameId: null,
            nickname: null,
          },
        ],
      });
      return;
    }

    const selectedNickname = selected.nickname;

    // Check if it's an existing nickname (has an id)
    if (selectedNickname.id) {
      onChange({
        eventClients: [
          {
            ...(summary?.eventClients?.[0] || {}),
            nicknameId: selectedNickname.id,
            nickname: { nickname: selectedNickname.name },
          },
        ],
      });
      return;
    }

    // New nickname — create it via API
    try {
      const res = await createClientNickname(clientId, selectedNickname.name);
      const created = res?.data;
      if (created) {
        // Update the client's nicknames list in local state
        const updatedNicknames = [
          ...(summary?.eventClients?.[0]?.client?.nicknames || []),
          { id: created.id, nickname: created.nickname },
        ];
        onChange({
          eventClients: [
            {
              ...(summary?.eventClients?.[0] || {}),
              client: {
                ...summary?.eventClients?.[0]?.client,
                nicknames: updatedNicknames,
              },
              nicknameId: created.id,
              nickname: { nickname: created.nickname },
            },
          ],
        });
      }
    } catch (err) {
      generateToast(
        err?.response?.data?.message || "Nickname must contain the client name",
        ERROR
      );
    }
  };

  // Nickname popover state
  const [nicknameAnchor, setNicknameAnchor] = useState(null);
  const [nicknameOptions, setNicknameOptions] = useState([]);
  const [nicknameError, setNicknameError] = useState("");

  const openNicknamePopover = async (e) => {
    setNicknameAnchor(e.currentTarget);
    setNicknameOptions([]);
    setNicknameError("");
    const clientId = summary?.eventClients?.[0]?.client?.id;
    if (clientId) {
      try {
        const res = await getClientNicknames(clientId);
        setNicknameOptions(
          (res?.data || []).map((n) => ({ id: n.id, name: n.nickname }))
        );
      } catch (err) {
        console.error("Failed to load nicknames", err);
      }
    }
  };

  const closeNicknamePopover = () => {
    setNicknameAnchor(null);
    setNicknameOptions([]);
    setNicknameError("");
  };

  const handleCollaborator = (e) => {
    onChange({
      eventClients: [
        { ...summary?.eventClients?.[0], collaborator: e.collaborator },
      ],
    });
  };

  const { role, firstName } = user || {};
  const allowedCreate = ["Olivia", "Lauren"].includes(firstName);
  const isAssistant = role?.toLowerCase() === "event director assistant";
  const isAdmin = executiveRoles.includes(role);

  const onSubmitForm = async () => {
    setIsLoading(true);

    if (!isParentForm && !summary?.clientType) {
      setIsLoading(false);
      return generateToast("Client Type is required!", ERROR);
    }

    if (!isParentForm && !summary?.startDate) {
      setIsLoading(false);
      return generateToast("Event Date is required!", ERROR);
    }

    if (!isParentForm && !summary?.region) {
      setIsLoading(false);
      return generateToast("Region is required!", ERROR);
    }

    if (!isParentForm && isAssistant && !summary?.soldBy) {
      setIsLoading(false);
      return generateToast("Sold by is required!", ERROR);
    }

    if (isParentForm && !summary?.title) {
      setIsLoading(false);
      return generateToast("Title by is required!", ERROR);
    }

    const filterSummary = Object.keys(summary)
      .filter(
        (key) =>
          summary?.[key] !== undefined &&
          summary?.[key] !== null &&
          summary?.[key] !== ""
      )
      .reduce((obj, key) => {
        obj[key] = summary[key];
        return obj;
      }, {});

    try {
      if (cloneData) {
        cloneContract(cloneData.id, extractIds(filterSummary))
          .then((res) => {
            dispatch(toggleTrigger());
            fetchDetails();
            handleDispatch();
            handleClick(res?.data);
            isParentForm && fetchParentOptions();
            setSummary({ startDate: "" });
            setShowForm(false);
            setIsLoading(false);
          })
          .catch((error) => {
            setIsLoading(false);
            generateToast(error.message || "Error cloning contract", ERROR);
          });
      } else {
        const contractsPromise = createContract(extractIds(filterSummary));
        contractsPromise
          .then((res) => {
            setIsLoading(false);
            dispatch(toggleTrigger());
            fetchDetails();
            handleDispatch();
            handleClick(res?.data);
            isParentForm && fetchParentOptions();
            setSummary({ startDate: "" });
            setShowForm(false);
          })
          .catch(({ data }) => {
            setIsLoading(false);
          });
      }
    } catch (error) {
      setIsLoading(false);
      generateToast(error.message || "Error cloning contract", ERROR);
    }
  };

  // Invokes updateClientType with the provided name and client type from the App context
  const handleClientTypeUpdate = (name) => {
    return updateClientType(name, summary?.clientType);
  };

  // Invokes updateClientName with the provided name and client type from the App context
  const handleClientUpdate = (name) => {
    return updateClientName(name, summary?.clientType?.id);
  };

  // Invokes updateRegion with the provided name and current region name from the App context
  const handleRegionUpdate = (name) => {
    return updateRegion(name);
  };

  // Invokes updateSchool with the provided name and current school name from the App context
  const handleSchoolUpdate = (name) => {
    return updateSchool(name);
  };

  useEffect(() => {
    if (!isAssistant) {
      onChange({
        soldBy: {
          id: user?.id,
          firstName: `${user?.firstName} ${user?.lastName}`,
        },
      });
    }
  }, [isAssistant]);

  useEffect(() => {
    if (cloneData) {
      setSummary({
        ...cloneData,
        startDate: cloneData.startDate,
        eventClients: cloneData.eventClients?.map(client => ({
          ...client,
          client: client.client,
          name: client.client?.name
        }))
      });
    }
  }, [cloneData]);

  return (
    <Modal size="xs" open={showForm} onClose={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title
          style={{
            fontWeight: 900,
            fontSize: "20px",
            textTransform: "none",
            color: mode === "dark" ? "white" : "black",
          }}
        >
          {cloneData ? "Clone Contract" : (!isParentForm ? "Create Contract Draft" : "Create Parent Contract")}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ position: "relative" }}>
        {/* Loading Overlay */}
        {isLoading && (
          <div
            style={{
              position: "absolute",
              zIndex: 10,
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              background: "rgba(255,255,255,0.7)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 8,
            }}
          >
            <Loader size="md" />
            <div style={{ marginTop: 12, fontWeight: 500, color: "#333" }}>
              Please wait...
            </div>
          </div>
        )}
        {/* The form itself, always rendered */}
        <>
          {isParentForm ? (
            <TextField
              size="small"
              sx={{
                mb: 0,
                m: 1,
                display: "flex",
                backgroundColor: mode === "dark" ? "#2B2B2B" : "white",
              }}
              onChange={(e) => onChange({ [e.target.name]: e.target.value })}
              type="text"
              name="title"
              label="Contract Title"
              value={summary.title}
            />
          ) : (
            <div className="tw-space-y-4">
              <EventDate
                format={"YYYY/MM/DD"}
                value={summary.startDate}
                name="startDate"
                set={onChange}
              />
              <AAutoComplete
                set={onChange}
                update={handleRegionUpdate}
                option={getRegion}
                value={summary.region?.name}
                name="region"
                labelValue="name"
                id="Region"
                label="Region"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PublicIcon className="tw-ml-2" />
                    </InputAdornment>
                  ),
                }}
              />
              <AAutoComplete
                set={onChange}
                sx={{ mt: 1 }}
                isNotCreatable={!isAdmin && !allowedCreate}
                update={handleClientTypeUpdate}
                option={getClientType}
                checkDisabled
                isCheckApproved
                value={summary?.clientType?.name}
                name="clientType"
                labelValue="name"
                id="Client Type"
                label="Client Type"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon className="tw-ml-2" />
                    </InputAdornment>
                  ),
                }}
              />
              <AAutoComplete
                set={onChange}
                sx={{ mt: 1 }}
                isNotCreatable={true}
                option={salesEmpWithOptions}
                value={summary.soldBy?.firstName}
                name="soldBy"
                labelValue="name"
                id="Sold By"
                label="Sold By"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <ShoppingCartIcon className="tw-ml-2" />
                    </InputAdornment>
                  ),
                }}
              />
              <Grid container spacing={0} sx={{ mt: -1 }}>
                <Grid item xs={12} md={12}>
                  <AAutoComplete
                    sx={{ mt: 1 }}
                    set={handelClient}
                    update={handleClientUpdate}
                    option={getClientName}
                    value={summary?.eventClients?.[0]?.name}
                    name="eventClients"
                    labelValue="name"
                    id="Client"
                    label={
                      summary?.eventClients?.[0]?.nickname?.nickname
                        ? `Client Name #1 — ${summary.eventClients[0].nickname.nickname}`
                        : "Client Name #1"
                    }
                    error={
                      summary?.clientType?.length > 1
                        ? ""
                        : "Select Client Type First"
                    }
                    InputProps={
                      summary?.eventClients?.[0]?.client?.id
                        ? {
                            startAdornment: (
                              <InputAdornment position="start" sx={{ mr: -0.5 }}>
                                <LabelIcon
                                  sx={{
                                    fontSize: 20,
                                    ml: 0.5,
                                    cursor: "pointer",
                                    color: summary?.eventClients?.[0]?.nickname?.nickname ? "primary.main" : "action.disabled",
                                    "&:hover": { color: "primary.main" },
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    openNicknamePopover(e);
                                  }}
                                />
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                  />
                </Grid>
                <Grid item xs={12} md={12}>
                  {isCollaboratorClientType(summary?.clientType?.name) && (
                    <>
                      <AAutoComplete
                        sx={{ mt: 1 }}
                        set={handleCollaborator}
                        update={handleSchoolUpdate}
                        option={getSchoolName}
                        value={summary?.eventClients?.[0]?.collaborator?.name}
                        name="collaborator"
                        labelValue="name"
                        id="School"
                        label="School"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SchoolIcon className="tw-ml-2" />
                            </InputAdornment>
                          ),
                        }}
                      />
                    </>
                  )}
                </Grid>
                <Grid item xs={1} md={1}></Grid>
              </Grid>
            </div>
          )}
        </>
      </Modal.Body>
      <Modal.Footer>
        <button
          onClick={handleClose}
          className="tw-px-4 tw-py-2 tw-rounded-lg tw-mr-2 tw-transition-colors"
          style={{
            backgroundColor: mode === "dark" ? "#2B2B2B" : "#F6F6F6",
            color: mode === "dark" ? "white" : "black",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = mode === "dark" ? "#3B3B3B" : "#E6E6E6";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = mode === "dark" ? "#2B2B2B" : "#F6F6F6";
          }}
        >
          Cancel
        </button>
        <button
          onClick={onSubmitForm}
          disabled={isLoading}
          className="tw-px-4 tw-py-2 tw-rounded-lg tw-transition-colors"
          style={{
            backgroundColor: "#48B0DB",
            color: "white",
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = "#398baf";
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = "#48B0DB";
          }}
        >
          {isLoading ? (
            <Loader size="sm" />
          ) : (
            cloneData ? "Clone Contract" : "Create Contract"
          )}
        </button>
      </Modal.Footer>

      {/* Nickname popover */}
      <Popover
        open={Boolean(nicknameAnchor)}
        anchorEl={nicknameAnchor}
        onClose={closeNicknamePopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Box sx={{ fontSize: "0.8rem", fontWeight: 600, mb: 1, color: "text.secondary" }}>
            Select or add a contract nickname
          </Box>
          <AAutoComplete
            set={(selected) => {
              if (!selected?.nickname) {
                // X was clicked — clear the nickname
                onChange({
                  eventClients: [
                    {
                      ...(summary?.eventClients?.[0] || {}),
                      nicknameId: null,
                      nickname: null,
                    },
                  ],
                });
                closeNicknamePopover();
                return;
              }
              handleNicknameSelect(selected);
              closeNicknamePopover();
            }}
            update={async (newName) => {
              const ec = summary?.eventClients?.[0];
              const clientId = ec?.client?.id;
              const clientName = ec?.client?.name || "";
              if (!clientId) return { isApproved: false };
              // Client-side pre-validation
              if (!newName.toLowerCase().includes(clientName.toLowerCase())) {
                setNicknameError(`Must contain "${clientName}"`);
                return { isApproved: false };
              }
              setNicknameError("");
              try {
                const res = await createClientNickname(clientId, newName);
                const created = res?.data;
                if (created) {
                  setNicknameOptions((prev) => [
                    ...prev,
                    { id: created.id, name: created.nickname },
                  ]);
                  onChange({
                    eventClients: [
                      {
                        ...(ec || {}),
                        nicknameId: created.id,
                        nickname: { nickname: created.nickname },
                      },
                    ],
                  });
                  closeNicknamePopover();
                  return { id: created.id, name: created.nickname, isApproved: true };
                }
              } catch (err) {
                setNicknameError(err?.response?.data?.message || "Failed to create nickname");
              }
              return { isApproved: false };
            }}
            isCheckApproved
            option={nicknameOptions}
            value={summary?.eventClients?.[0]?.nickname?.nickname || ""}
            name="nickname"
            labelValue="name"
            isUpperCaseQuery
            id="Nickname-popover"
            label="Contract Name"
          />
          {nicknameError ? (
            <Box sx={{ fontSize: "0.75rem", mt: 0.5, color: "error.main" }}>
              {nicknameError}
            </Box>
          ) : (
            <Box sx={{ fontSize: "0.7rem", mt: 0.5, color: "text.disabled" }}>
              New nickname must contain the client's name
            </Box>
          )}
        </Box>
      </Popover>
    </Modal>
  );
};

export default ContactForm;
